import React from "react";
import { Label } from "../../../../shadcn/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../shadcn/ui/tooltip";
import { HelpCircle, Trash2 } from "lucide-react";
import logger from "../../../../utils/logger";


const ImageUploadSection = ({
  watchedImages,
  dragActive,
  fileInputRef,
  handleDrag,
  handleDrop,
  handleFileSelect,
  removeImage,
  errors,
  isReadOnly = false,
}) => {
  const FieldTip = ({ children }) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-4 h-4 ml-1 text-gray-400 cursor-help" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        {children}
      </TooltipContent>
    </Tooltip>
  );

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label className="flex items-center">
            Space Images{" "}
            <FieldTip>
              Upload up to 8 images of your space (max 5MB each). High-quality photos help attract more bookings.
            </FieldTip>
          </Label>
          <span className="text-xs text-gray-500">{watchedImages.length}/8 images (max 5MB each)</span>
        </div>

        {/* Drag and Drop Area */}
        <div
          className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer ${
            dragActive ? "border-blue-500 bg-blue-50" : ""
          } ${watchedImages.length >= 8 || isReadOnly ? "opacity-50 pointer-events-none" : ""}`}
          onDragEnter={isReadOnly ? undefined : handleDrag}
          onDragLeave={isReadOnly ? undefined : handleDrag}
          onDragOver={isReadOnly ? undefined : handleDrag}
          onDrop={isReadOnly ? undefined : handleDrop}
          onClick={() => !isReadOnly && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={watchedImages.length >= 8 || isReadOnly}
            style={{ display: "none" }}
          />
          <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="text-gray-500 text-sm mb-2">
            {isReadOnly
              ? "Image upload is disabled in read-only mode"
              : watchedImages.length >= 8
                ? "Maximum images reached"
                : "Drag and Drop images here"}
          </p>
          {watchedImages.length < 8 && !isReadOnly && (
            <>
              <p className="text-gray-500 text-sm mb-2">or</p>
              <span className="text-blue-600 hover:text-blue-700 text-sm font-medium">Browse Files</span>
              {/* <button
              type="button"
              onClick={() => {
                logger.log("📸 [DEBUG] Browse button clicked");
                if (fileInputRef.current) {
                  logger.log("📸 [DEBUG] File input ref exists, triggering click");
                  fileInputRef.current.click();
                } else {
                  logger.log("📸 [DEBUG] File input ref is null");
                }
              }}
              className="ml-2 text-xs text-gray-500 hover:text-gray-700 underline"
            >
              (Debug: Test Click)
            </button> */}
            </>
          )}
          <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF, WEBP up to 5MB</p>
        </div>

        {/* Error message for images */}
        {errors.images && <p className="text-sm text-red-500">{errors.images.message}</p>}

        {/* Uploaded Images Grid */}
        {watchedImages.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {watchedImages.map(image => (
              <div key={image.id} className="relative group">
                <img src={image.url} alt={image.name} className="w-full h-20 object-cover rounded-lg border" />
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => removeImage(image.id)}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-75 group-hover:opacity-100 hover:bg-red-600 transition-all duration-200 shadow-sm"
                    title={`Remove ${image.name}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
                <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded">
                  {(image.size / 1024).toFixed(0)}KB
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Show message when no images uploaded */}
        {watchedImages.length === 0 && (
          <div className="text-center py-3">
            <p className="text-gray-500 text-sm">No images uploaded yet</p>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ImageUploadSection;
